import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig, isMsalConfigured } from "./msalConfig";

/**
 * Singleton MSAL instance.
 *
 * Always constructed (so `<MsalProvider instance={msalInstance}>` is happy),
 * but only used when `isMsalConfigured()` is true. The dev sign-in path
 * ignores this instance entirely.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * True when this window was opened by msalInstance.loginPopup. Such windows
 * should NOT attempt their own ssoSilent / app initialisation, because they
 * exist solely to receive the auth code and post it back to the opener.
 */
export const isInsideMsalPopup = () =>
  Boolean(window.opener) && /[?#].*(code|error)=/.test(window.location.hash + window.location.search);

/**
 * True when running inside an iframe (e.g. Emergent preview shell).
 * MSAL redirect flow cannot work here because Microsoft's login page sets
 * X-Frame-Options: DENY. We treat this environment as a dev-mode fallback.
 */
export const isInsideIframe = () => {
  try {
    return window.self !== window.top;
  } catch (_) {
    return true;
  }
};

// MSAL v5 requires explicit initialize(); track readiness so the API client
// can await it before attempting silent token acquisition. Also process any
// pending redirect response (after returning from Microsoft sign-in).
export const msalReady = msalInstance
  .initialize()
  .then(() => msalInstance.handleRedirectPromise())
  .then((result) => {
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
    } else {
      // Pick up an existing account after refresh (sessionStorage)
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
    // Keep active account synced on login success
    msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload &&
        event.payload.account
      ) {
        msalInstance.setActiveAccount(event.payload.account);
      }
    });
  })
  .catch((err) => {
    // eslint-disable-next-line no-console
    console.error("[msal] init/redirect handling failed", err);
  });

export { isMsalConfigured };

